<script setup lang="ts">
    import BaseSlider from '@/components/BaseSlider.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import BaseInput from '@/components/BaseInput.vue';
    import { useElementPosition } from '@/composables/useElementPosition';
    import { useSpacingControl } from '@/composables/useSpacingControl';
    import { ref, useTemplateRef, toRef } from 'vue';
    import { onClickOutside } from '@vueuse/core';
    import type { ITextField } from '@/typescript/interfaces/ITextField';

    const props = defineProps<{
        field: ITextField;
        max: string;
        min: string;
        spacingType: 'characterSpacing' | 'lineSpacing';
        title?: string;
    }>();

    const spacingModalRef = useTemplateRef('spacingModal');
    const modalContentRef = useTemplateRef('modalContent');
    onClickOutside(spacingModalRef, (event) => {
        if (modalContentRef.value && modalContentRef.value.contains(event.target as Node)) {
            return;
        }
        isOpen.value = false;
    });
    const { style } = useElementPosition(spacingModalRef);
    const isOpen = ref(false);

    const { spacingValue } = useSpacingControl(
        toRef(() => props.field),
        toRef(() => props.spacingType),
    );
</script>

<template>
    <div
        ref="spacingModal"
        class="relative"
    >
        <BaseButton
            class="h-8 w-8 border-0"
            @click="isOpen = !isOpen"
        >
            <slot />
        </BaseButton>
        <transition
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            class="duration-300"
        >
            <div v-if="isOpen">
                <Teleport to="body">
                    <div
                        ref="modalContent"
                        :style
                        class="absolute -left-36 top-14 z-10 flex w-[20rem] items-center justify-center bg-white p-5 shadow-dropdown"
                    >
                        <div class="text-center">
                            <h2 class="mb-4 text-xl text-black">{{ props.title }}</h2>

                            <div class="flex items-center px-2">
                                <BaseSlider
                                    v-model="spacingValue"
                                    :min
                                    :max
                                    label-id="spacing-slider"
                                    :show-value="false"
                                />
                                <BaseInput
                                    v-model="spacingValue"
                                    name="spacing_value"
                                    :show-label="false"
                                    type="number"
                                    :label="$t('spacing_value')"
                                    :class="[
                                        'ml-8 mr-5 flex h-[40px] w-[45px] flex-none items-center',
                                        'justify-center border border-black bg-white pl-1.5',
                                    ]"
                                />
                            </div>
                        </div>
                    </div>
                </Teleport>
            </div>
        </transition>
    </div>
</template>
